"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, Flame, Droplets, Volume2, Gauge, Sparkles, PhoneCall, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface SymptomItem {
  id: string;
  title: string;
  badge: string;
  description: string;
  cause: string;
  actionText: string;
  linkPath: string;
  iconName: "Flame" | "Droplets" | "Volume2" | "Gauge" | "Sparkles" | "AlertTriangle";
}
const symptomsList: SymptomItem[] = [{
  id: "no-hot-water",
  title: "No Hot Water at All",
  badge: "High Urgency",
  description: "Water stays cold even after running taps. Often indicates heating element failure, tripped breaker, or thermocouple issue.",
  cause: "Broken heating element, faulty thermostat, or unlit pilot.",
  actionText: "Water heater repair",
  linkPath: "/water-heater-repair/",
  iconName: "Flame"
}, {
  id: "water-leaking",
  title: "Water Leaking from Tank",
  badge: "Emergency Risk",
  description: "Puddles or pooling around the water heater base. Requires immediate shutdown to prevent property damage.",
  cause: "Corroded tank base, failed T&P relief valve, or loose fittings.",
  actionText: "Emergency plumbing help",
  linkPath: "/emergency-plumbing/",
  iconName: "Droplets"
}, {
  id: "popping-noises",
  title: "Knocking or Popping Sounds",
  badge: "Maintenance Need",
  description: "Loud rumbling or boiling sounds coming from the unit during heating cycles. Trapped steam bubbles bursting under scale.",
  cause: "Heavy sediment buildup in the bottom of the water tank.",
  actionText: "Schedule tank service",
  linkPath: "/water-heater-repair/",
  iconName: "Volume2"
}, {
  id: "rusty-water",
  title: "Rusty or Discolored Water",
  badge: "Corrosion Alert",
  description: "Brown, yellow, or murky water only from the hot side of fixtures, indicating internal degradation inside the system.",
  cause: "Failing sacrificial anode rod or deteriorating internal tank walls.",
  actionText: "Inspect or replace tank",
  linkPath: "/water-heater-replacement/",
  iconName: "AlertTriangle"
}, {
  id: "low-pressure",
  title: "Weak Hot Water Pressure",
  badge: "Performance Issue",
  description: "Cold water flows normally, but hot water pressure is weak and restricted across showers and faucets.",
  cause: "Sediment blockages in hot water supply pipes or shut-off valves.",
  actionText: "Clean & restore pressure",
  linkPath: "/water-heater-repair/",
  iconName: "Gauge"
}, {
  id: "lukewarm-water",
  title: "Lukewarm or Short Supply",
  badge: "Element Failure",
  description: "Hot water runs out in just a few minutes, or never reaches full temperature despite normal thermostat settings.",
  cause: "Burned out upper/lower element or broken dip tube.",
  actionText: "Explore tankless or repair",
  linkPath: "/tankless-water-heater-service/",
  iconName: "Sparkles"
}];
export default function Symptoms() {
  const prefersReducedMotion = useReducedMotion();
  const getIcon = (name: SymptomItem["iconName"]) => {
    switch (name) {
      case "Flame":
        return <Flame className="size-5 text-accent" />;
      case "Droplets":
        return <Droplets className="size-5 text-accent" />;
      case "Volume2":
        return <Volume2 className="size-5 text-accent" />;
      case "AlertTriangle":
        return <AlertTriangle className="size-5 text-accent" />;
      case "Gauge":
        return <Gauge className="size-5 text-accent" />;
      case "Sparkles":
        return <Sparkles className="size-5 text-accent" />;
      default:
        return <AlertTriangle className="size-5 text-accent" />;
    }
  };
  return <section id="waterheatersymptoms" data-nav="light" className="bg-background py-16 sm:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold text-xs tracking-wide uppercase">
            Troubleshooting Guide
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Common Water Heater Symptoms
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Identify what is wrong with your hot water system. Our licensed Jonesboro plumbers provide same-day diagnostics and fast repairs.
          </p>
        </div>

        {/* Symptoms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {symptomsList.map((item, i) => <div key={item.id} data-index={i} className="h-full">
              <motion.div initial={prefersReducedMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={prefersReducedMotion ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: i * 0.08
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border shadow-xs hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40 bg-card text-card-foreground">
                  <CardHeader className="space-y-3 pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        {getIcon(item.iconName)}
                      </div>
                      <Badge variant="outline" className="text-xs font-medium border-border">
                        {item.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground font-default pt-1">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2 pb-4">
                    <div className="rounded-md bg-muted/60 p-3 text-xs sm:text-sm text-foreground">
                      <span className="font-semibold text-primary block mb-0.5">Likely Cause:</span>
                      {item.cause}
                    </div>
                  </CardContent>

                  <CardFooter className="pt-0">
                    <Button asChild variant="outline" className="w-full justify-between group">
                      <Link to={item.linkPath} className="flex items-center justify-between w-full">
                        <span>{item.actionText}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* Bottom Callout Banner */}
        <div className="mt-12 sm:mt-16 bg-muted/50 border border-border rounded-2xl p-6 sm:p-8 flex flex-col lg:flex-row items-center justify-between gap-6 text-center lg:text-left">
          <div className="max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-bold font-default text-foreground">
              Not sure what is wrong with your water heater?
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              Do not risk scalding water or flooding. Speak with a Jonesboro technician now for immediate phone guidance and prompt on-site dispatch.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-4" />
                <span>Contact Plumber Now</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/water-heater/">
                <span>View All Services</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}